import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { APP_ROUTES } from '../../../../constants/routes';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import api from '../../../../services/httpClient';
import { VACANCY_ENDPOINTS } from '../../../../services/endpoints/rec/vacancy.endpoints';
import Button from '../../../common/Button';
import ConfirmationModal from '../../../common/ConfirmationModal';
import DatePickerField from '../../../common/DatePickerField';
import FileUploadField from '../../../common/FileUploadField';
import Loading from '../../../common/Loading';
import TextField from '../../../common/TextField';
import type { VacancyApiResponse } from '../../../../services/types/vacancy.types';
import { isValidUrl } from '../../../../utils/common';
import { buildUploadFormData } from '../../../../utils/vacancyFormData';

interface VacancyUploadFormProps {
  type: 'add' | 'edit';
  addedVacancyId?: number | null;
  onCancel: () => void;
  onSuccess: (message?: string) => void;
  onError: (message?: string) => void;
}

const SOCIAL_MEDIA_DESC_MAX = 255;

const VacancyUploadForm: React.FC<VacancyUploadFormProps> = ({ type, addedVacancyId, onCancel, onSuccess, onError }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [vacancyUrl, setVacancyUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [socialMedia, setSocialMedia] = useState('');
  const [publishedDate, setPublishedDate] = useState('');
  const [vacancyTinyUrl, setVacancyTinyUrl] = useState('');
  const [approvalStatus, setApprovalStatus] = useState(0);
  const [socialMediaTags, setSocialMediaTags] = useState('');
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [approvalInitialStatus, setApprovalInitialStatus] = useState(0);
  const [linkedinImage, setLinkedinImage] = useState<File | null | string>(null);
  const [facebookImage, setFacebookImage] = useState<File | null | string>(null);
  const [instagramImage, setInstagramImage] = useState<File | null | string>(null);
  
  // Confirmation modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if ((type === 'add' && pageId !== 9) || (type === 'edit' && pageId !== 12)) {
      setPageId(type === 'add' ? 9 : 12);
      return;
    }

    const vacancyId = params?.id;
    if (type === 'add' || vacancyId === undefined || vacancyId === null) return;

    setIsLoading(true);
    api.get<VacancyApiResponse>(
      VACANCY_ENDPOINTS.byId(vacancyId),
      {
        headers: { ...API_HEADERS.VACANCY_FORM_DEFAULT },
      }
    ).then((response) => {
      const data = response.data;

      setVacancyUrl(data.vacancyUrl);
      setVacancyTinyUrl(data.vacancyTinyUrl);
      setSocialMedia(data.socialMediaDesc);
      setSocialMediaTags(data.socialMediaTags);
      setLinkedinImage(data.imageLinkedIn);
      setFacebookImage(data.imageFB);
      setInstagramImage(data.imageInstagram);
      setPublishedDate(data.publishedDate);
      setApprovalInitialStatus(data.approvalStatus);
      setApprovalStatus(data.approvalStatus);
      setIsLoading(false);
    });
  }, [type, params.id, pageId, setPageId]);

  const buildFormData = (includeApprovalStatus: boolean = true): FormData =>
    buildUploadFormData(
      vacancyUrl,
      vacancyTinyUrl,
      socialMedia,
      socialMediaTags,
      publishedDate,
      approvalStatus,
      approvalInitialStatus,
      linkedinImage,
      facebookImage,
      instagramImage,
      includeApprovalStatus,
    );

  const handleSave = async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};

    if (!isValidUrl(vacancyUrl)) {
      newErrors.vacancyUrl = 'Invalid URL';
    }

    if (!isValidUrl(vacancyTinyUrl)) {
      newErrors.vacancyTinyUrl = 'Invalid URL';
    }

    if (socialMedia.length > SOCIAL_MEDIA_DESC_MAX) {
      newErrors.socialMedia = `Maximum ${SOCIAL_MEDIA_DESC_MAX} characters allowed`;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length >= 1) {
      onError('Please fix the errors before saving.');
      // Return true so Save onClick does not navigate away (same as "confirmation required" guard).
      return true;
    }

    try {
      const vacancyId = addedVacancyId || params.id;
      if (!vacancyId) return false;

      setIsSaving(true);
      const formData = buildFormData(true);
      // First call without forceUnpublish flag

      const response = await api.put<VacancyApiResponse & { requiresConfirmation?: boolean }>(
        VACANCY_ENDPOINTS.updateMedia(vacancyId),
        formData,
        {
          headers: {
            ...(type === 'add' ? API_HEADERS.VACANCY_UPLOAD_MEDIA_SAVE_ADD : API_HEADERS.VACANCY_UPLOAD_MEDIA_SAVE_EDIT),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        // Check if confirmation is required
        if (response.data.requiresConfirmation) {
          setIsConfirmModalOpen(true);
          setIsSaving(false);
          return true; // Return true to indicate confirmation is required
        } else {
          onSuccess('Vacancy updated successfully!');
          setIsSaving(false);
          return false; // Return false to indicate save completed successfully
        }
      }
        return false;
      } catch (error: any) {
        // Safety check: Handle if API sends 400 for published vacancies
        if (error.response?.status === 400 && error.response?.data?.requiresConfirmation) {
          setIsConfirmModalOpen(true);
          setIsSaving(false);
          return true;
        }

        console.error('Error updating vacancy:', error);
        onError(error.response?.data?.message || 'Failed to update vacancy. Please try again.');
        setIsSaving(false);
        return false;
      }
  };

  const handleConfirmUpdate = async () => {
    try {
      setIsConfirming(true);
      const vacancyId = addedVacancyId || params.id;
      if (!vacancyId) return;

      // Rebuild formData without approvalStatus, with forceUnpublish flag
      const formData = buildFormData(false);
      formData.append('forceUnpublish', 'true');

      const response = await api.put<VacancyApiResponse>(
        VACANCY_ENDPOINTS.updateMedia(vacancyId),
        formData,
        {
          headers: {
            ...(type === 'add' ? API_HEADERS.VACANCY_UPLOAD_MEDIA_SAVE_ADD : API_HEADERS.VACANCY_UPLOAD_MEDIA_SAVE_EDIT),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        // Update local state to reflect unpublished and approval pending
        setApprovalStatus(0);
        setApprovalInitialStatus(0);
        
        // Always call deleteJob API to remove from website (same as world icon unpublish)
        try {
          await api.post(VACANCY_ENDPOINTS.deleteJob(vacancyId), {}, {
            headers: { ...API_HEADERS.VACANCY_SEARCH_DELETE_JOB },
          });
        } catch (deleteError) {
          console.error('Error unpublishing from website:', deleteError);
          // Continue anyway - the internal unpublish worked
        }
        
        setIsConfirmModalOpen(false);
        onSuccess('Vacancy updated successfully! The vacancy has been unpublished and approval status is now pending.');
        
        // Navigate to search page after confirmation (since approvalStatus was likely >= 1 before)
        navigate(APP_ROUTES.VACANCY.SEARCH);
      }
    } catch (error: any) {
      console.error('Error confirming update:', error);
      onError('Failed to confirm update. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      const vacancyId = addedVacancyId || params.id;
      if (!vacancyId) return;

      await handleSave();

      setIsApproving(true);
      const response = await api.put(VACANCY_ENDPOINTS.approve(vacancyId), {}, {
        headers: { ...API_HEADERS.VACANCY_UPLOAD_APPROVE },
      });
      if (response.status === 200) {
        onSuccess('Vacancy approved successfully!');
        navigate(APP_ROUTES.VACANCY.SEARCH);
      }
    } catch (error) {
      console.log('Error approving vacancy:', error);
      onError('Failed to approve vacancy. Please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <Box gap={3} display="flex" flexDirection="column">
      {isLoading ? <Loading /> : (
        <>
          <Box className="edit-section">
            <Grid container gap={"62px"}>
              <Grid>
                <TextField
                  label="Vacancy URL"
                  name="vacancyUrl"
                  width="400px"
                  value={vacancyUrl}
                  onChange={(e) => {
                    setVacancyUrl(e.target.value);
                    setErrors({ ...errors, vacancyUrl: '' });
                  }}
                  error={!!errors.vacancyUrl}
                  helperText={errors.vacancyUrl}
                />
              </Grid>
              <Grid>
                <TextField
                  label="Vacancy Tiny URL"
                  name="vacancyTinyUrl"
                  width="400px"
                  value={vacancyTinyUrl}
                  onChange={(e) => {
                    setVacancyTinyUrl(e.target.value);
                    setErrors({ ...errors, vacancyTinyUrl: '' });
                  }}
                  error={!!errors.vacancyTinyUrl}
                  helperText={errors.vacancyTinyUrl}
                />
              </Grid>
              <Grid>
              <DatePickerField
                width="400px"
                value={publishedDate && publishedDate !== "0000-00-00" ? dayjs(publishedDate) : null}
                label="Vacancy Published Date"
                onChange={(date) => setPublishedDate(date || '')}
              />
              </Grid>
            </Grid>
          </Box>

          <Box className="edit-section">
            <Box sx={{ mb: 3, width: '1323px' }}>
              <Box>
                <TextField
                  label="Social Media Description"
                  name="socialMedia"
                  rows={3}
                  multiline
                  value={socialMedia}
                  onChange={(e) => {
                    setSocialMedia(e.target.value);
                    setErrors((prev) => ({ ...prev, socialMedia: '' }));
                  }}
                  inputProps={{ maxLength: SOCIAL_MEDIA_DESC_MAX }}
                  error={!!errors.socialMedia}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      height: 'auto',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: errors.socialMedia ? 'space-between' : 'flex-end', mt: 0.5 }}>
                  {errors.socialMedia && (
                    <Typography sx={{ fontSize: '12px', color: '#E90000' }}>
                      {errors.socialMedia}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)' }}>
                    {socialMedia.length}/{SOCIAL_MEDIA_DESC_MAX}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Grid container sx={{ width: '1323px' }}>
              <Grid sx={{ marginRight: '62px' }}>
                <TextField
                  label="Social Media Tags"
                  name="socialMediaTags"
                  width="400px"
                  value={socialMediaTags}
                  onChange={(e) => setSocialMediaTags(e.target.value)}
                />
              </Grid>
              <Grid container gap="19px">
                <Grid>
                  <FileUploadField
                    label="Linkedin Image"
                    width="201px"
                    value={linkedinImage}
                    onChange={(file) => setLinkedinImage(file)}
                    accept="image/*"
                  />
                </Grid>
                <Grid>
                  <FileUploadField
                    label="Facebook Image"
                    width="201px"
                    value={facebookImage}
                    onChange={(file) => setFacebookImage(file)}
                    accept="image/*"
                  />
                </Grid>
                <Grid>
                  <FileUploadField
                    label="Instagram Image"
                    width="201px"
                    value={instagramImage}
                    onChange={(file) => setInstagramImage(file)}
                    accept="image/*"
                  />
                </Grid>
              </Grid>
              <Grid sx={{ marginLeft: '34px' }}>
                <FormControlLabel
                  label="Ready for Approval"
                  sx={{ color: '#00000066', fontSize: '15px', fontWeight: 500, marginTop: '17px' }}
                  control={
                    <Checkbox
                      name="approvalStatus"
                      checked={approvalStatus >= 1 && approvalStatus <= 2}
                      onChange={(e) => setApprovalStatus(e.target.checked ? 2 : 0)}
                      disabled={approvalInitialStatus === 1}
                      sx={{
                        color: '#0D9C33',
                        '&.Mui-checked': {
                          color: '#0D9C33',
                        },
                        '&.Mui-disabled': {
                          color: '#00000066',
                        },
                      }}
                    />
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ width: '1347px', display: 'flex', justifyContent: 'flex-end', alignItems: 'end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSaving || isApproving}
              className="edit-section-cancel-button"
            >
              Back
            </Button>
            
            {/* Save button logic */}
            {((type === 'add' && hasPermission(PERMISSION_IDS.VACANCY_UPLOAD_SAVE_ADD)) || 
              (type === 'edit' && hasPermission(PERMISSION_IDS.VACANCY_UPLOAD_SAVE_EDIT) && 
                (
                  hasPermission(PERMISSION_IDS.VACANCY_UPLOAD_APPROVAL_ACTION)
                    ? (approvalInitialStatus === 0 || approvalInitialStatus === 1 || approvalInitialStatus === 2 || approvalInitialStatus === 3)
                    : (approvalInitialStatus === 0 || approvalInitialStatus === 3)
                )
              )) && (
              <Button
                variant="contained"
                onClick={async () => {
                  // handleSave returns true if a popup is shown, false if it saved directly
                  const confirmationRequired = await handleSave();
                  
                  // If no confirmation popup was needed, it means the save is done.
                  // We can now safely redirect to the search page.
                  if (!confirmationRequired) {
                    navigate(APP_ROUTES.VACANCY.SEARCH);
                  }
                }}
                className="edit-section-save-button"
                disabled={isSaving || isApproving}
                isLoading={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            )}
            
            {/* Approve button - Admin & Rec Admin only, status 2 */}
            {hasPermission(PERMISSION_IDS.VACANCY_UPLOAD_APPROVAL_ACTION) && approvalInitialStatus === 2 && (
              <Button
                isLoading={isApproving}
                className="edit-section-save-button"
                variant="contained"
                onClick={handleApprove}
                disabled={isApproving || isSaving}
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </Button>
            )}
          </Box>
        </>
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        open={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
        }}
        onConfirm={handleConfirmUpdate}
        title="Unpublish Vacancy?"
        message="If you want to do these changes, the vacancy will be unpublished. Do you want to continue?"
        confirmText="Yes"
        cancelText="No"
        isActioning={isConfirming}
      />
    </Box>
  );
};

export default VacancyUploadForm; 